// ABOUTME: Shared types and route/selector helpers for the live page editor overlay layer.
// ABOUTME: Overrides are stored per route pattern and resolved against the rendered DOM.

export type OverrideKind = 'style' | 'insert' | 'replace';

/** Style/content patch — the only kind implemented today. */
export interface StylePatch {
  /** Replacement text for a leaf element */
  text?: string;
  /** Desktop inline styles (camelCase-free: raw CSS property names) */
  style?: Record<string, string>;
  /** Styles applied only below 640px */
  styleMobile?: Record<string, string>;
  /** Hide the element for everyone */
  hidden?: boolean;
  /** Target index of the element among its parent's children */
  order?: number;
}

export interface PageOverride {
  id: string;
  route_pattern: string;
  selector: string;
  kind: OverrideKind;
  patch: StylePatch;
  position: string | null;
  sort_order: number;
}

/** Draft override before it has a database id. */
export type OverrideDraft = Omit<PageOverride, 'id'> & { id?: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Turns a concrete pathname into a route pattern so an edit made on one course
 * page applies to every course page: id-like segments collapse to ":id".
 */
export const routePatternOf = (pathname: string): string => {
  const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (segments.length === 0) return '/';
  return '/' + segments.map((s) => (UUID_RE.test(s) || /^\d+$/.test(s) ? ':id' : s)).join('/');
};

/** Elements owned by the editor itself are never editable nor part of selectors. */
export const EDITOR_ROOT_ID = 'live-editor-root';

export const isEditorNode = (el: Element | null): boolean =>
  !!el?.closest(`#${EDITOR_ROOT_ID}, [data-live-editor-ui="true"]`);

/**
 * Builds a stable CSS path for an element, e.g. `body>div:nth-of-type(1)>main>h1`.
 * Prefers an explicit `data-po-id` when the markup provides one.
 */
export const buildSelector = (el: Element): string => {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node !== document.documentElement) {
    const explicit = node.getAttribute('data-po-id');
    if (explicit) {
      parts.unshift(`[data-po-id="${explicit}"]`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
    parts.unshift(sameTag.length > 1 ? `${tag}:nth-of-type(${sameTag.indexOf(node) + 1})` : tag);
    node = parent;
  }
  return parts.join('>');
};

/** Resolves a stored selector back to an element, or null when the markup changed. */
export const resolveSelector = (selector: string, root: ParentNode = document): Element | null => {
  try {
    return root.querySelector(selector);
  } catch {
    return null;
  }
};

/** Deterministic short key used for the mobile-styles CSS hook. */
export const selectorKey = (selector: string): string => {
  let hash = 0;
  for (let i = 0; i < selector.length; i += 1) {
    hash = (hash * 31 + selector.charCodeAt(i)) | 0;
  }
  return `po${Math.abs(hash).toString(36)}`;
};
